import React, { useEffect, useRef } from 'react';

const ConstellationCanvas = () => {
    const canvasRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;
        let particles = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initParticles();
        };

        const initParticles = () => {
            const particleCount = Math.min(Math.floor(window.innerWidth / 20), 100);
            particles = [];
            for (let i = 0; i < particleCount; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    vx: (Math.random() - 0.5) * 0.5,
                    vy: (Math.random() - 0.5) * 0.5,
                    size: Math.random() * 2 + 1,
                    hue: Math.random() > 0.5 ? 210 : 160
                });
            }
        };

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'lighter';

            particles.forEach((p, i) => {
                p.x += p.vx;
                p.y += p.vy;

                if (p.x < 0) p.x = canvas.width;
                if (p.x > canvas.width) p.x = 0;
                if (p.y < 0) p.y = canvas.height;
                if (p.y > canvas.height) p.y = 0;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fillStyle = `hsla(${p.hue}, 100%, 70%, 0.8)`;
                ctx.fill();

                const dx = p.x - mouseRef.current.x;
                const dy = p.y - mouseRef.current.y;
                const mouseDist = Math.sqrt(dx * dx + dy * dy);
                const interactionRadius = 300;

                if (mouseDist < interactionRadius) {
                    const opacity = 1 - (mouseDist / interactionRadius);
                    ctx.beginPath();
                    ctx.strokeStyle = `rgba(56, 189, 248, ${opacity})`;
                    ctx.lineWidth = 2 * opacity;
                    ctx.moveTo(p.x, p.y);
                    ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
                    ctx.stroke();

                    const angle = Math.atan2(dy, dx);
                    const force = (interactionRadius - mouseDist) / interactionRadius;
                    const pullFactor = 1.5;

                    p.x += Math.cos(angle) * force * pullFactor * 0.5;
                    p.y += Math.sin(angle) * force * pullFactor * 0.5;
                }

                for (let j = i + 1; j < particles.length; j++) {
                    const p2 = particles[j];
                    const pdx = p.x - p2.x;
                    const pdy = p.y - p2.y;

                    if (Math.abs(pdx) > 120 || Math.abs(pdy) > 120) continue;

                    const pDist = Math.sqrt(pdx * pdx + pdy * pdy);

                    if (pDist < 120) {
                        ctx.beginPath();
                        const lineOpacity = 0.15 * (1 - pDist / 120);
                        ctx.strokeStyle = `hsla(${p.hue}, 100%, 80%, ${lineOpacity})`;
                        ctx.lineWidth = 0.5;
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(p2.x, p2.y);
                        ctx.stroke();
                    }
                }
            });

            animationFrameId = requestAnimationFrame(draw);
        };

        const handleMouseMove = (e) => {
            const rect = canvas.getBoundingClientRect();
            mouseRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        };

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', handleMouseMove);

        resize();
        draw();

        return () => {
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', handleMouseMove);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="absolute inset-0 z-0 pointer-events-none mix-blend-screen" />;
};

export default ConstellationCanvas;
